import {
  every,
  isEmpty,
  keys,
  orderBy,
  pick,
  reduce,
  toLower,
  words
} from "lodash";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import ContactComponent from "../../../components/ContactComponent";
import FilterLabels from "../../../components/labels/FilterLabels";
import { getLabelColorStyle } from "../../../components/labels/utils";
import { useAppearance } from "../../../context/appearance";
import { useAuthentication } from "../../../context/authentication";
import { useCommissionReports } from "../../../context/commissionReports";
import { useContacts } from "../../../context/contacts";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import {
  getSmartLabelColor,
  getSmartLabelFromKey
} from "../../../context/filteredList/FilteredListProvider";
import { useLabels } from "../../../context/labels";
import {
  LoadingKey,
  useLoading
} from "../../../context/loading/LoadingProvider";
import { Entity } from "../../../utils/constants";
import { downloadCsv } from "../../../utils/csv";
import {
  CommissionPaymentStatus,
  CommissionReport,
  ContactCommissions
} from "../../../utils/types";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";
import useDebounce from "../../../utils/useDebounce";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import { BONES_PER_HNT, useHntMarket } from "../../../utils/useHntMarket";
import EntityDataTable from "../common/EntityDataTable";
import PaymentMethodComponent from "../common/PaymentMethodComponent";
import TablePropertiesSidebar from "../common/TablePropertiesSidebar";
import TableSearchInput from "../common/TableSearchInput";
import useDataTable, { Property } from "../common/useDataTable";
import { PaginationKey } from "../common/usePagination";
import { enhancedPaymentMethods } from "../ContactsData/PaymentMethodFormSidebar";

interface Props {
  commissionReport: CommissionReport;
  getPaymentStatus: (
    contactCommissions: ContactCommissions
  ) => CommissionPaymentStatus;
}

const CommissionReportContacts: React.FC<Props> = ({
  commissionReport,
  getPaymentStatus
}) => {
  const { isDark } = useAppearance();
  const { contacts } = useContacts();
  const { getIsReady } = useLoading();
  const { wrapActionWithAuth } = useAuthentication();
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const { getDefaultPaymentMethod } = useContacts();
  const { currencyDisplay } = useCurrency();
  const { labels, linkedLabels } = useLabels();
  const [filteredLabels, toggleFilteredLabel] = useFilteredLabels((state) => [
    state.filteredLabels,
    state.toggleFilteredLabel
  ]);
  const sortOrder = useDataTable(
    (state) => state.sortOrder[Entity.contactCommissions]
  );
  const searchInput = useDataTable(
    (state) => state.searchInput[Entity.contactCommissions]
  );
  const debouncedSearchInput = useDebounce(searchInput, 200);
  const { displayInCurrency } = useHntMarket();
  const {
    commissionReports,
    setContactCommissionsSummarySidebarConfig,
    setCommissionPaymentSummarySidebarConfig
  } = useCommissionReports();

  const properties: { [key: string]: Property<ContactCommissions> } = useMemo(
    () => ({
      contact: {
        key: "contact",
        title: "Contacts",
        style: { minWidth: "20rem" },
        sortIteratee: "contact.name",
        renderContent: ({ contact }) => (
          <Link
            className="relative flex items-center space-x-3 group"
            to={`/contacts/${contact.uuid}`}
          >
            <ContactComponent
              contactId={contact.uuid}
              fallbackName={contact.name}
            />
          </Link>
        )
      },
      defaultPaymentMethod: {
        key: "defaultPaymentMethod",
        title: "Payment method",
        style: { minWidth: "14rem" },
        renderContent: ({ contact }) => {
          const defaultPaymentMethod = getDefaultPaymentMethod(
            contact.uuid,
            commissionReport.currency
          );
          return defaultPaymentMethod ? (
            <PaymentMethodComponent
              paymentMethod={defaultPaymentMethod}
              withoutDefaultLabel
            />
          ) : null;
        }
      },
      status: {
        key: "status",
        title: "Status",
        renderContent: (contactCommission) => {
          const { status } = getPaymentStatus(contactCommission);

          if (status === "loading") return "loading";

          return (
            <div
              className="inline-flex items-center rounded-full font-medium px-3 text-sm cursor-pointer"
              style={getLabelColorStyle(getSmartLabelColor(status), isDark)}
              onClick={() => {
                toggleFilteredLabel(LabelFilterKey.contactCommissions, status);
              }}
            >
              {status}
            </div>
          );
        }
      },
      totalCommissionValue: {
        key: "totalCommissionValue",
        title: "Commission in period",
        style: { minWidth: "15rem" },
        sortIteratee: "totalCommissionValue",
        renderContent: ({ totalCommissionValue }) =>
          commissionReport.currency === CurrencyKey.hnt
            ? displayInCurrency(totalCommissionValue, {
                withHntSuffix: true,
                needsDivision: true
              })
            : currencyDisplay(commissionReport.currency, totalCommissionValue)
      },
      commissions: {
        key: "commissions",
        title: "Commissions",
        style: { minWidth: "15rem" },
        renderContent: ({ commissions }) => commissions.length
      },
      open: {
        key: "open",
        title: "Details",
        style: { minWidth: "9rem" },
        renderContent: (contactCommissions) => (
          <button
            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
            onClick={() => {
              setContactCommissionsSummarySidebarConfig({
                show: true,
                commissionReport,
                contactCommissions,
                getPaymentStatus
              });
            }}
          >
            Open details
          </button>
        )
      }
    }),
    [
      commissionReport,
      currencyDisplay,
      displayInCurrency,
      getDefaultPaymentMethod,
      getPaymentStatus,
      isDark,
      setContactCommissionsSummarySidebarConfig,
      toggleFilteredLabel
    ]
  );

  const isLoading = useMemo(
    () =>
      isEmpty(commissionReports) &&
      !getIsReady([LoadingKey.workspaceCommissionReports]),
    [commissionReports, getIsReady]
  );

  const smartLabelFilters: {
    [key: string]: (contactCommission: ContactCommissions) => boolean;
  } = useMemo(
    () => ({
      unpaid: (x) => getPaymentStatus(x).status === "unpaid",
      failed: (x) => getPaymentStatus(x).status === "failed",
      paid: (x) => getPaymentStatus(x).status === "paid"
    }),
    [getPaymentStatus]
  );

  const filteredContactCommissionLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.contactCommissions],
        labels,
        smartLabelFilters,
        Entity.contact
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredContactCommissions = useMemo(() => {
    let filteredList: ContactCommissions[] = [];

    if (commissionReport.report) {
      filteredList = commissionReport.report.filter((contactCommission) => {
        if (
          !isEmpty(filteredContactCommissionLabels) &&
          !every(filteredContactCommissionLabels, (label) => {
            const smartFilterFunction = (smartLabelFilters as any)[label.uuid]; // TODO: fix typing

            return smartFilterFunction
              ? smartFilterFunction(contactCommission)
              : (linkedLabels[contactCommission.contact.uuid] || []).includes(
                  label.uuid
                );
          })
        ) {
          return false;
        }

        if (debouncedSearchInput) {
          const searchInputWords = words(debouncedSearchInput).map(toLower);

          return every(searchInputWords, (word) =>
            toLower(contactCommission.contact.name).includes(word)
          );
        }
        return true;
      });
    }

    if (sortOrder) {
      filteredList = orderBy(
        filteredList,
        [sortOrder.iteratee],
        [sortOrder.order]
      );
    }

    return filteredList;
  }, [
    commissionReport.report,
    debouncedSearchInput,
    filteredContactCommissionLabels,
    linkedLabels,
    smartLabelFilters,
    sortOrder
  ]);

  const exportCsv = useCallback(
    (contactCommissions: ContactCommissions[]) => {
      const contactPaymentsToExport = contactCommissions.map(
        (contactCommission) => {
          const paymentMethod = getDefaultPaymentMethod(
            contactCommission.contact.uuid,
            commissionReport.currency
          );

          const enhancedPaymentMethod = paymentMethod
            ? enhancedPaymentMethods[paymentMethod.type]
            : null;

          const paymentMethodFields =
            paymentMethod && enhancedPaymentMethod
              ? reduce(
                  pick(
                    JSON.parse(paymentMethod.metadata) || {},
                    enhancedPaymentMethod.fields
                  ),
                  (acc: { [key: string]: string }, value, key) => {
                    acc[`${paymentMethod.type}_${key}`] = value;
                    return acc;
                  },
                  { payment_method: enhancedPaymentMethod.title }
                )
              : {};

          const fullContact = contacts[contactCommission.contact.uuid];

          const { status } = getPaymentStatus(contactCommission);

          return {
            name: fullContact?.name || contactCommission.contact.name || "",
            email: fullContact?.email || "",
            phone: fullContact?.phone || "",
            amount:
              commissionReport.currency === CurrencyKey.hnt
                ? contactCommission.totalCommissionValue / BONES_PER_HNT
                : contactCommission.totalCommissionValue,
            currency: commissionReport.currency,
            status,
            ...paymentMethodFields
          };
        }
      );

      downloadCsv(
        contactPaymentsToExport,
        `commission-report-${commissionReport.identifier}-${
          commissionReport.currency
        }-payments-${moment().utc().format("YYYY_MM_DD_HH_mm")}`
      );
    },
    [
      commissionReport.currency,
      commissionReport.identifier,
      contacts,
      getDefaultPaymentMethod,
      getPaymentStatus
    ]
  );

  const renderTable = useMemo(() => {
    if (isLoading) return <CenteredLoading className="h-96" />;

    return (
      <>
        <div className="px-4 p-4">
          <TableSearchInput
            entity={Entity.contactCommissions}
            placeholder="Search contacts by name, email or phone"
          />

          <div className="mt-2 flex items-center">
            <FilterLabels
              className="flex-1"
              entity={Entity.contact}
              filteredLabels={filteredContactCommissionLabels}
              smartLabels={keys(smartLabelFilters).map((key) =>
                getSmartLabelFromKey(key, Entity.contactCommissions)
              )}
              filterKey={LabelFilterKey.contactCommissions}
            />
            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                },
                {
                  disabled: isEmpty(filteredContactCommissions),
                  onClick: () => {
                    exportCsv(filteredContactCommissions);
                  },
                  text: `Export ${
                    filteredContactCommissions.length <
                    commissionReport?.report?.length
                      ? "filtered"
                      : "all"
                  } contact commissions`
                }
              ]}
            />

            {!isEmpty(filteredContactCommissions) && (
              <button
                type="button"
                className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-500"
                onClick={wrapActionWithAuth(() => {
                  setCommissionPaymentSummarySidebarConfig({
                    show: true,
                    commissionReport,
                    contactCommissionsToPay: filteredContactCommissions,
                    getPaymentStatus
                  });
                })}
              >
                Pay{" "}
                {filteredContactCommissions.length <
                commissionReport.report.length
                  ? "filtered"
                  : "all"}{" "}
                contacts
              </button>
            )}
          </div>
        </div>

        <EntityDataTable<ContactCommissions>
          paginationKey={PaginationKey.workspaceCommissionReportContacts}
          entity={Entity.contactCommissions}
          items={filteredContactCommissions}
          properties={properties}
          selectionActions={[
            {
              title: "Pay selected contacts",
              callback: (selectedContactCommissions: ContactCommissions[]) => {
                setCommissionPaymentSummarySidebarConfig({
                  show: true,
                  commissionReport,
                  contactCommissionsToPay: selectedContactCommissions,
                  getPaymentStatus
                });
              }
            }
          ]}
        />
      </>
    );
  }, [
    commissionReport,
    exportCsv,
    filteredContactCommissionLabels,
    filteredContactCommissions,
    getPaymentStatus,
    isLoading,
    properties,
    setCommissionPaymentSummarySidebarConfig,
    smartLabelFilters,
    wrapActionWithAuth
  ]);

  return (
    <>
      <TablePropertiesSidebar<ContactCommissions>
        entity={Entity.contactCommissions}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      {renderTable}
    </>
  );
};

export default CommissionReportContacts;
